import React, { useEffect } from 'react';
import { XIcon, LockIcon, SparklesIcon } from 'lucide-react';
import { PlanTier, FeatureKey } from '../../types/entitlements';
import { upsellCopy, trackUpsellEvent } from '../../utils/upsellCopy';
import { getRequiredPlan } from '../../hooks/useEntitlements';
interface PaywallOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  feature: FeatureKey;
  onUpgrade: (plan: PlanTier) => void;
}
export function PaywallOverlay({
  isOpen,
  onClose,
  feature,
  onUpgrade
}: PaywallOverlayProps) {
  if (!isOpen) return null;
  const requiredPlan = getRequiredPlan(feature);
  const planDetails = upsellCopy.plansDetailed[requiredPlan as keyof typeof upsellCopy.plansDetailed];
  const featureName = upsellCopy.featureNames[feature];
  const handleUpgrade = () => {
    trackUpsellEvent('upsell_click', {
      surface: 'paywall_overlay',
      cta: 'upgrade',
      plan: requiredPlan,
      feature
    });
    onUpgrade(requiredPlan);
  };
  useEffect(() => {
    if (isOpen) {
      trackUpsellEvent('upsell_impression', {
        surface: 'paywall_overlay',
        feature,
        planSuggested: requiredPlan
      });
    }
  }, [isOpen, feature, requiredPlan]);
  return <>
      <div className="fixed inset-0 bg-black bg-opacity-60 z-50" onClick={onClose} data-id="element-3531" />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-3532">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden" data-id="element-3533">
          <div className="relative bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-12 text-white" data-id="element-3534">
            <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors" data-id="element-3535">
              <XIcon className="w-6 h-6" data-id="element-3536" />
            </button>
            <div className="flex items-center gap-3 mb-4" data-id="element-3537">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center" data-id="element-3538">
                <LockIcon className="w-6 h-6" data-id="element-3539" />
              </div>
              <div data-id="element-3540">
                <h2 className="text-2xl font-bold" data-id="element-3541">{featureName}</h2>
                <p className="text-purple-100" data-id="element-3542">Available on {requiredPlan}</p>
              </div>
            </div>
            <p className="text-lg text-purple-50" data-id="element-3543">
              {upsellCopy.roiSnippets[feature]}
            </p>
          </div>
          <div className="p-8" data-id="element-3544">
            <div className="mb-6" data-id="element-3545">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-3546">
                {planDetails.title}
              </h3>
              <div className="grid grid-cols-1 gap-3" data-id="element-3547">
                {planDetails.benefits.map((benefit, index) => <div key={index} className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg" data-id="element-3548">
                    <SparklesIcon className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" data-id="element-3549" />
                    <span className="text-sm text-gray-700" data-id="element-3550">{benefit}</span>
                  </div>)}
              </div>
            </div>
            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-4 mb-6" data-id="element-3551">
              <p className="text-sm font-semibold text-gray-900 mb-2" data-id="element-3552">
                Estimated Impact
              </p>
              <div className="grid grid-cols-3 gap-4 text-center" data-id="element-3553">
                <div data-id="element-3554">
                  <p className="text-2xl font-bold text-green-600" data-id="element-3555">+25%</p>
                  <p className="text-xs text-gray-600" data-id="element-3556">Pipeline Lift</p>
                </div>
                <div data-id="element-3557">
                  <p className="text-2xl font-bold text-blue-600" data-id="element-3558">20 hrs</p>
                  <p className="text-xs text-gray-600" data-id="element-3559">Time Saved/Week</p>
                </div>
                <div data-id="element-3560">
                  <p className="text-2xl font-bold text-purple-600" data-id="element-3561">2.3 mo</p>
                  <p className="text-xs text-gray-600" data-id="element-3562">Payback Period</p>
                </div>
              </div>
            </div>
            <button onClick={handleUpgrade} className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all font-semibold text-lg" data-id="element-3563">
              Upgrade to {requiredPlan}
            </button>
          </div>
        </div>
      </div>
    </>;
}